import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

import { submitParceiroToHubSpot } from "@/lib/hubspot";
import {
  trackCompleteRegistration,
  trackInitiateCheckout,
  trackLead,
  trackLeadDesqualificado,
  trackMQL,
} from "@/lib/meta-pixel";
import {
  CARTEIRA_OPTIONS,
  CERTIFICACAO_OPTIONS,
  parceirosFormSchema,
  type ParceirosFormData,
} from "./schema";
import { cn } from "@/lib/utils";
import { formatPhone } from "@/lib/phone";
import { buildWhatsappLink, WHATSAPP_PARCEIROS } from "@/lib/whatsapp";
import { WhatsappIcon } from "@/components/icons/WhatsappIcon";

// Score arbitrário do lead baseado no tamanho da carteira (BRL).
// Serve como sinal pro Meta otimizar a campanha por valor estimado.
const CARTEIRA_VALUE_MAP: Record<string, number> = {
  ate1milhao: 1,
  "1a5milhoes": 5,
  "5a20milhoes": 20,
  "20a100milhoes": 100,
  acima100milhoes: 500,
};

// Faixas que qualificam como MQL (carteira ≥ R$ 5M).
const MQL_CARTEIRAS = new Set([
  "5a20milhoes",
  "20a100milhoes",
  "acima100milhoes",
]);

type FormState = Partial<ParceirosFormData>;
type FieldErrors = Partial<Record<keyof ParceirosFormData, string>>;

const inputBase =
  "h-12 sm:h-[52px] bg-white border-transparent text-[#161617] placeholder:text-zinc-400 focus:border-[#4BD084] focus-visible:ring-[#4BD084] focus-visible:ring-offset-[#112711] rounded-xl text-base";

export const ParceirosHeroForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const trackedFirstFocus = useRef(false);

  const [data, setData] = useState<FormState>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const set = <K extends keyof ParceirosFormData>(
    key: K,
    value: ParceirosFormData[K]
  ) => {
    setData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const onFirstFocus = () => {
    if (trackedFirstFocus.current) return;
    trackInitiateCheckout({
      content_name: "Programa de Parceiros Allu",
      content_category: "parceiros",
    });
    trackedFirstFocus.current = true;
  };

  // Pergunta qualificadora: "sim" libera o select de carteira (limpa pra
  // escolher); "nao" trava o select e marca a carteira como "nao_tem".
  const setJaAtua = (v: "sim" | "nao") => {
    set("ja_atua", v);
    set("carteira_size", v === "nao" ? "nao_tem" : "");
  };

  // Valida um campo no blur e mostra só o erro dele — evita o botão ficar
  // cinza "sem explicação" quando um campo está preenchido mas inválido
  // (ex.: e-mail malformado).
  const validateField = (key: keyof ParceirosFormData) => {
    const parsed = parceirosFormSchema.safeParse(data);
    const issue = parsed.success
      ? undefined
      : parsed.error.issues.find((i) => i.path[0] === key);
    setErrors((prev) => {
      const next = { ...prev };
      if (issue) next[key] = issue.message;
      else delete next[key];
      return next;
    });
  };

  // Estado reativo que dirige o botão.
  const isValid = parceirosFormSchema.safeParse(data).success;
  const isMQL = data.carteira_size
    ? MQL_CARTEIRAS.has(data.carteira_size)
    : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parceirosFormSchema.safeParse(data);

    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      parsed.error.issues.forEach((issue) => {
        const key = issue.path[0] as keyof ParceirosFormData;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      });
      setErrors(fieldErrors);
      toast({
        title: "Confira os dados",
        description:
          parsed.error.issues[0]?.message ?? "Preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    // Dispara a conversão no Pixel ANTES do envio pro HubSpot.
    // Motivo: o submit passa por uma Edge Function (browser → /api/parceiros-lead →
    // HubSpot CRM), que pode levar 1-2s. Se o trackLead viesse depois desse await e
    // logo antes do navigate, o beacon do fbq podia não sair a tempo (race com a
    // navegação SPA) — resultado: lead no HubSpot, mas 0 resultado no Meta.
    // Disparando aqui, o evento sai no clique, com folga, independente do backend.
    const value = CARTEIRA_VALUE_MAP[parsed.data.carteira_size] ?? 1;

    // event_id compartilhado: o mesmo Lead sai do Pixel (browser) e do CAPI
    // (servidor, em /api/parceiros-lead). Meta deduplica pelo event_id, então
    // a cobertura sobe sem contar lead duas vezes. Pro MQL, sufixo "-mql" pra
    // distinguir (event_id é único POR nome de evento).
    const eventID =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Advanced Matching — fbq normaliza/hasheia internamente; passar valor cru.
    const [firstName, ...lastParts] = parsed.data.fullname.trim().split(/\s+/);
    const userData = {
      em: parsed.data.email,
      ph: parsed.data.phone.replace(/\D/g, ""),
      fn: firstName,
      ln: lastParts.join(" ") || undefined,
      country: "br",
    };

    // "Não atuo no mercado" (carteira nao_tem) = lead ruim pro Meta. NÃO dispara
    // o "Lead" padrão (senão o algoritmo otimiza buscando mais gente assim) —
    // dispara LeadDesqualificado só pra registro/exclusão de público.
    const isDesqualificado = parsed.data.carteira_size === "nao_tem";

    if (isDesqualificado) {
      trackLeadDesqualificado(
        {
          content_name: "Programa de Parceiros Allu",
          content_category: "parceiros",
          currency: "BRL",
          value: 0,
        },
        { eventID, userData }
      );
    } else {
      trackLead(
        {
          content_name: "Programa de Parceiros Allu",
          content_category: "parceiros",
          currency: "BRL",
          value,
          carteira_size: parsed.data.carteira_size,
          lead_quality: isMQL ? "mql" : "regular",
        },
        { eventID, userData }
      );
    }

    if (isMQL) {
      trackMQL(
        {
          content_name: "Programa de Parceiros Allu",
          carteira_size: parsed.data.carteira_size,
          currency: "BRL",
          value,
        },
        { eventID: `${eventID}-mql`, userData }
      );
    }

    try {
      await submitParceiroToHubSpot(parsed.data, eventID);

      // Lead de carteira qualificada (MQL) → direto pro WhatsApp da equipe.
      // O HubSpot já capturou acima, então mesmo que a pessoa não mande a
      // mensagem, o lead está no CRM e a equipe liga.
      if (isMQL) {
        const carteiraLabel =
          CARTEIRA_OPTIONS.find((o) => o.value === parsed.data.carteira_size)
            ?.label ?? "";
        const msg = `Olá! Meu nome é ${parsed.data.fullname}, tenho uma carteira de ${carteiraLabel} e gostaria de conhecer o programa de parceiros do Allu Invest.`;
        trackCompleteRegistration({
          content_name: "Programa de Parceiros Allu",
          content_category: "parceiros",
        });
        window.location.href = buildWhatsappLink(msg, WHATSAPP_PARCEIROS);
        return;
      }

      // O flag avisa a página de obrigado pra NÃO disparar CompleteRegistration
      // — desqualificado não pode contar como conversão em nenhum evento padrão.
      navigate("/parceiros/obrigado", {
        state: isDesqualificado ? { desqualificado: true } : undefined,
      });
    } catch (err) {
      console.error("[Parceiros] HubSpot submit error:", err);
      toast({
        title: "Erro ao enviar",
        description:
          "Tente novamente em instantes. Se persistir, fale com a gente pelo e-mail.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="form"
      onSubmit={handleSubmit}
      noValidate
      className="bg-[#112711] rounded-3xl p-6 sm:p-7 w-full shadow-2xl shadow-[#112711]/25 ring-1 ring-[#1f4a2b]/50"
    >
      <div className="mb-5">
        <h2
          className="text-xl sm:text-2xl font-semibold text-white tracking-tight"
          style={{
            fontFamily:
              '"Plus Jakarta Sans", Inter, system-ui, sans-serif',
            letterSpacing: "-0.02em",
          }}
        >
          Aplicação para parceria
        </h2>
        <p className="text-sm text-[#D1F8E2] mt-1.5 opacity-80">
          Retornamos em até 1 dia útil para uma conversa 1:1.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label
            htmlFor="fullname"
            className="text-white text-sm font-medium"
          >
            Nome completo <span className="text-[#4BD084]">*</span>
          </Label>
          <Input
            id="fullname"
            value={data.fullname ?? ""}
            onChange={(e) => set("fullname", e.target.value)}
            onFocus={onFirstFocus}
            onBlur={() => validateField("fullname")}
            placeholder="Seu nome e sobrenome"
            autoComplete="name"
            className={inputBase}
            aria-invalid={!!errors.fullname}
          />
          {errors.fullname && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.fullname}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-white text-sm font-medium">
            E-mail profissional <span className="text-[#4BD084]">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={data.email ?? ""}
            onChange={(e) => set("email", e.target.value)}
            onFocus={onFirstFocus}
            onBlur={() => validateField("email")}
            placeholder="voce@empresa.com.br"
            autoComplete="email"
            className={inputBase}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.email}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-white text-sm font-medium">
            Telefone (com DDD) <span className="text-[#4BD084]">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            value={data.phone ?? ""}
            onChange={(e) => set("phone", formatPhone(e.target.value))}
            onFocus={onFirstFocus}
            onBlur={() => validateField("phone")}
            placeholder="(11) 99999-9999"
            autoComplete="tel"
            className={inputBase}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-white text-sm font-medium">
            Você já trabalha no mercado de investimentos?{" "}
            <span className="text-[#4BD084]">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-3">
            {(["sim", "nao"] as const).map((opt) => {
              const active = data.ja_atua === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onFirstFocus();
                    setJaAtua(opt);
                  }}
                  aria-pressed={active}
                  className={cn(
                    "h-12 sm:h-[52px] rounded-xl text-base font-medium border transition-colors",
                    active
                      ? "bg-[#4BD084] text-[#112711] border-[#4BD084]"
                      : "bg-white text-[#161617] border-transparent hover:bg-zinc-50"
                  )}
                >
                  {opt === "sim" ? "Sim" : "Não"}
                </button>
              );
            })}
          </div>
          {errors.ja_atua && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.ja_atua}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="certificacao"
            className="text-white text-sm font-medium"
          >
            Qual certificação do mercado financeiro você possui?{" "}
            <span className="text-[#D1F8E2]/50 font-normal">(opcional)</span>
          </Label>
          <Select
            value={data.certificacao ?? ""}
            onValueChange={(v) => set("certificacao", v)}
          >
            <SelectTrigger
              id="certificacao"
              className={inputBase}
              onFocus={onFirstFocus}
            >
              <SelectValue placeholder="Selecione uma opção" />
            </SelectTrigger>
            <SelectContent>
              {CERTIFICACAO_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="carteira_size"
            className="text-white text-sm font-medium"
          >
            Tamanho da carteira sob custódia atualmente{" "}
            <span className="text-[#4BD084]">*</span>
          </Label>
          {data.ja_atua === "sim" ? (
            <Select
              value={data.carteira_size ?? ""}
              onValueChange={(v) => set("carteira_size", v)}
            >
              <SelectTrigger
                id="carteira_size"
                className={inputBase}
                aria-invalid={!!errors.carteira_size}
                onFocus={onFirstFocus}
              >
                <SelectValue placeholder="Selecione uma faixa" />
              </SelectTrigger>
              <SelectContent>
                {CARTEIRA_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div
              aria-disabled="true"
              className={cn(
                inputBase,
                "flex items-center px-4 bg-white/50 text-zinc-400 cursor-not-allowed select-none"
              )}
            >
              {data.ja_atua === "nao"
                ? "Não se aplica"
                : "Responda a pergunta acima"}
            </div>
          )}
          {errors.carteira_size && (
            <p className="text-xs text-[#fca5a5] mt-1">{errors.carteira_size}</p>
          )}
        </div>

        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <Checkbox
            id="lgpd_consent"
            checked={!!data.lgpd_consent}
            onCheckedChange={(v) => set("lgpd_consent", v === true)}
            className="mt-0.5 border-zinc-400 data-[state=checked]:bg-[#4BD084] data-[state=checked]:border-[#4BD084] data-[state=checked]:text-[#112711]"
            aria-invalid={!!errors.lgpd_consent}
          />
          <span className="text-xs text-[#D1F8E2]/80 leading-relaxed">
            Li e autorizo o tratamento dos meus dados conforme a{" "}
            <a
              href="/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white underline underline-offset-2 hover:text-[#4BD084]"
            >
              Política de Privacidade
            </a>
            .
          </span>
        </label>
        {errors.lgpd_consent && (
          <p className="text-xs text-[#fca5a5]">{errors.lgpd_consent}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !isValid}
          className={cn(
            "w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm h-12 sm:h-[52px] shadow-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#112711]",
            isValid
              ? "bg-[#4BD084] hover:bg-[#5fdc94] text-[#112711] focus-visible:ring-[#4BD084]"
              : "bg-zinc-300 text-zinc-500 cursor-not-allowed"
          )}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando…
            </>
          ) : isMQL ? (
            <>
              <WhatsappIcon className="w-4 h-4" />
              Falar no WhatsApp agora
            </>
          ) : (
            <>
              Quero receber contato
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {!isValid ? (
          <p className="text-[11px] text-[#D1F8E2]/70 text-center leading-relaxed pt-1">
            Preencha todos os campos para continuar.
          </p>
        ) : isMQL ? (
          <p className="text-[11px] text-[#D1F8E2]/70 text-center leading-relaxed pt-1">
            Você será direcionado ao WhatsApp da nossa equipe.
          </p>
        ) : null}

        <p className="text-[11px] text-[#D1F8E2]/60 text-center leading-relaxed pt-1">
          Conteúdo institucional B2B. Não destinado ao investidor final.
        </p>
      </div>
    </form>
  );
};
